import type { AuthorityRef, ProviderId, SessionSummary } from "./domain/session.ts";
import { projectSession } from "./domain/project-session.ts";
import { decodeSessionSnapshot, wireArray, wireRecord } from "./wire/session-codec.ts";
import {
  decodeAgentIdentity,
  decodeProviderId,
  type AgentId,
  type CliVariantId,
  type HistoryScanState,
} from "./agent-protocol.ts";

export type ProviderHistoryImportStatus = "all" | "unimported" | "imported";

export interface ProviderHistoryTargetOptions {
  deviceId?: string;
  importStatus?: ProviderHistoryImportStatus;
  /** Restrict automatic discovery to one CLI provider. */
  provider?: ProviderId;
  /** Bypass the short-lived transport acceleration cache. */
  refresh?: boolean;
  /** Zero-based page offset for large Provider history catalogs. */
  cursor?: number;
  /** Opaque page cursor bound to the Daemon history index revision. */
  cursorToken?: string;
  /** Maximum conversations returned in one catalog page. */
  limit?: number;
}

export type ProviderHistoryTarget = string | ProviderHistoryTargetOptions;

export interface ProviderHistorySourceInput {
  provider: ProviderId;
  homeDirectory: string;
}

export interface ProviderHistoryConversation {
  sourceId: string;
  provider: ProviderId;
  /** Daemon-owned source home used to read this conversation. */
  historyHomeDirectory?: string;
  /** Canonical Agent identity derived at the transport boundary. */
  agentId: AgentId;
  cliVariantId: CliVariantId;
  conversationId: string;
  workspacePath: string;
  title: string;
  preview: string;
  messageCount: number;
  userMessageCount: number;
  summaryTruncated: boolean;
  startedAt: string | null;
  updatedAt: string | null;
  resumeSupported: boolean;
  imported: boolean;
  importedSessionId: string | null;
  importedAuthority: AuthorityRef | null;
  importedDeviceId: string | null;
}

/**
 * Return a stable activity timestamp for history rows. Provider history may
 * omit updated_at for an incomplete record, so started_at is the best
 * available fallback. Invalid timestamps sort below records with a real
 * timestamp.
 */
export function providerHistoryActivityTime(
  conversation: Pick<ProviderHistoryConversation, "updatedAt" | "startedAt">,
): number {
  for (const value of [conversation.updatedAt, conversation.startedAt]) {
    if (!value) continue;
    const timestamp = Date.parse(value);
    if (Number.isFinite(timestamp)) return timestamp;
  }
  return Number.NEGATIVE_INFINITY;
}

/** Sort history globally by most recently updated conversation first. */
export function sortProviderHistoryConversations<
  T extends Pick<ProviderHistoryConversation, "sourceId" | "updatedAt" | "startedAt">,
>(conversations: readonly T[]): T[] {
  return [...conversations].sort((left, right) => {
    const leftTime = providerHistoryActivityTime(left);
    const rightTime = providerHistoryActivityTime(right);
    if (leftTime !== rightTime) return rightTime - leftTime;
    return left.sourceId.localeCompare(right.sourceId);
  });
}

export interface ProviderHistoryWorkspace {
  workspacePath: string;
  displayName: string;
  conversationCount: number;
  updatedAt: string | null;
  conversations: ProviderHistoryConversation[];
}

export interface ProviderHistoryCatalogItem {
  provider: ProviderId;
  homeDirectory: string;
  conversationCount: number;
  workspaces: ProviderHistoryWorkspace[];
  error: string | null;
}

export interface ProviderHistoryCatalog {
  conversationCount: number;
  providers: ProviderHistoryCatalogItem[];
  /** Present when the Daemon returned a bounded catalog page. */
  nextCursor?: number | null;
  /** Revision used to mint `nextCursorToken`, when the Daemon has an index. */
  indexRevision?: number;
  /** Opaque revision-bound cursor for the next page. */
  nextCursorToken?: string | null;
  /** Total rows matching the requested import filter (before paging). */
  totalConversationCount?: number;
  /** True when no additional catalog page is available. */
  complete?: boolean;
}

export interface ProviderHistoryScanResult {
  workspacePath: string;
  conversations: ProviderHistoryConversation[];
}

/** Durable status returned by the Daemon-owned history scanner. */
export interface ProviderHistoryScanCheckpoint {
  provider: string;
  historyRoot: string;
  scannedFiles: number;
  indexedItems: number;
  lastSourceId: string | null;
}

export interface ProviderHistoryScanOperation {
  schemaVersion: number;
  correlationId: string;
  operationId: string;
  state: HistoryScanState;
  phase: string;
  importFilter: ProviderHistoryImportStatus;
  checkpoints: ProviderHistoryScanCheckpoint[];
  indexRevision: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
  errorCode: string | null;
}

export interface ProviderHistoryScanPollOptions {
  /** Maximum time to wait for a terminal scan state. */
  timeoutMs?: number;
  /** Delay between status reads. */
  intervalMs?: number;
  signal?: AbortSignal;
}

export interface ProviderHistoryMessage {
  messageId: string;
  ordinal: number;
  role: "user" | "assistant" | "tool" | "system";
  text: string;
  createdAt: string | null;
}

export interface ProviderHistoryConversationPage {
  conversation: ProviderHistoryConversation;
  messages: ProviderHistoryMessage[];
  nextCursor: number | null;
}

export interface ProviderHistoryReadInput {
  workspacePath: string;
  provider: ProviderId;
  conversationId: string;
  /** Provider home used for discovery; omitted for platform default. */
  historyHomeDirectory?: string;
  cursor?: number;
  limit?: number;
}

export interface ProviderHistoryImportInput {
  provider: ProviderId;
  historyHomeDirectory?: string;
  sourceId?: string;
  conversationId: string;
  workspacePath: string;
  title?: string;
}

export interface ProviderHistoryImportResultItem {
  provider: ProviderId;
  sourceId: string;
  conversationId: string;
  created: boolean;
  session: SessionSummary;
}

export interface ProviderHistoryImportResult {
  registryRevision: number;
  imports: ProviderHistoryImportResultItem[];
  sessions: SessionSummary[];
}

export interface ProviderHistoryTransport {
  discoverProviderHistory(
    sources?: ProviderHistorySourceInput[],
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryCatalog>;
  scanProviderHistory(
    workspacePath: string,
    providers?: ProviderId[],
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanResult>;
  /** Start a cancellable, Daemon-owned history scan. */
  createProviderHistoryScan?(
    workspacePath: string,
    providers?: ProviderId[],
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation>;
  getProviderHistoryScan?(
    operationId: string,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation>;
  cancelProviderHistoryScan?(
    operationId: string,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation>;
  resumeProviderHistoryScan?(
    operationId: string,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryScanOperation>;
  pollProviderHistoryScan?(
    operationId: string,
    target?: ProviderHistoryTarget,
    options?: ProviderHistoryScanPollOptions,
  ): Promise<ProviderHistoryScanOperation>;
  readProviderConversation(
    input: ProviderHistoryReadInput,
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryConversationPage>;
  importProviderHistory(
    conversations: ProviderHistoryImportInput[],
    target?: ProviderHistoryTarget,
  ): Promise<ProviderHistoryImportResult>;
}

/**
 * Keep discovery compatible with older Daemons/Cloud command schemas that do
 * not understand the optional top-level `provider` selector.  Transports may
 * discover the complete catalog and apply this projection locally instead.
 */
export function filterProviderHistoryCatalog(
  catalog: ProviderHistoryCatalog,
  provider?: ProviderId,
): ProviderHistoryCatalog {
  if (!provider) return catalog;
  const providers = catalog.providers.filter((item) => item.provider === provider);
  return {
    conversationCount: providers.reduce((total, item) => total + item.conversationCount, 0),
    providers,
    // Keep paging metadata when projecting a provider-scoped response. Losing
    // next_cursor here made Mobile treat the first page as complete and never
    // hydrate the remainder of a large Codex catalog.
    nextCursor: catalog.nextCursor,
    indexRevision: catalog.indexRevision,
    nextCursorToken: catalog.nextCursorToken,
    totalConversationCount: catalog.totalConversationCount,
    complete: catalog.complete,
  };
}

export function providerHistoryTarget(
  target?: ProviderHistoryTarget,
): Required<Pick<ProviderHistoryTargetOptions, "importStatus">> & Pick<ProviderHistoryTargetOptions, "deviceId" | "provider" | "refresh" | "cursor" | "cursorToken" | "limit"> {
  if (typeof target === "string") return { deviceId: target, importStatus: "all" };
  const cursor = target?.cursor;
  const cursorToken = target?.cursorToken?.trim();
  const limit = target?.limit;
  if (cursor !== undefined && (!Number.isSafeInteger(cursor) || cursor < 0)) {
    throw new TypeError("Provider history cursor must be a non-negative integer");
  }
  if (cursorToken !== undefined && !cursorToken) {
    throw new TypeError("Provider history cursor token must not be empty");
  }
  if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 1 || limit > 500)) {
    throw new TypeError("Provider history limit must be between 1 and 500");
  }
  return {
    ...(target?.deviceId ? { deviceId: target.deviceId } : {}),
    importStatus: target?.importStatus ?? "all",
    ...(target?.provider ? { provider: target.provider } : {}),
    ...(target?.refresh ? { refresh: true } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(cursorToken ? { cursorToken } : {}),
    ...(limit !== undefined ? { limit } : {}),
  };
}

export function decodeProviderHistoryCatalog(
  value: unknown,
  fallbackAuthority: AuthorityRef,
  fallbackDeviceId?: string,
): ProviderHistoryCatalog {
  const root = wireRecord(value, "ProviderHistoryCatalog");
  return {
    conversationCount: integer(root.conversation_count, "conversation_count"),
    nextCursor: root.next_cursor === undefined || root.next_cursor === null
      ? null
      : integer(root.next_cursor, "next_cursor"),
    indexRevision: root.index_revision === undefined || root.index_revision === null
      ? undefined
      : integer(root.index_revision, "index_revision"),
    nextCursorToken: root.next_cursor_token === undefined || root.next_cursor_token === null
      ? null
      : text(root.next_cursor_token, "next_cursor_token"),
    totalConversationCount: root.total_conversation_count === undefined
      ? undefined
      : integer(root.total_conversation_count, "total_conversation_count"),
    complete: root.complete === undefined ? undefined : boolean(root.complete, "complete"),
    providers: wireArray(root.providers, "providers").map((value, providerIndex) => {
      const provider = wireRecord(value, `providers[${providerIndex}]`);
      return {
        provider: providerId(provider.provider, `providers[${providerIndex}].provider`),
        homeDirectory: text(provider.home_directory, `providers[${providerIndex}].home_directory`),
        conversationCount: integer(provider.conversation_count, `providers[${providerIndex}].conversation_count`),
        error: nullableText(provider.error, `providers[${providerIndex}].error`),
        workspaces: wireArray(provider.workspaces, `providers[${providerIndex}].workspaces`)
          .map((value, workspaceIndex) => decodeWorkspace(
            value,
            `providers[${providerIndex}].workspaces[${workspaceIndex}]`,
            text(provider.home_directory, `providers[${providerIndex}].home_directory`),
            fallbackAuthority,
            fallbackDeviceId,
          )),
      };
    }),
  };
}

export function decodeProviderHistoryScan(
  value: unknown,
  fallbackAuthority: AuthorityRef,
  fallbackDeviceId?: string,
): ProviderHistoryScanResult {
  const root = wireRecord(value, "ProviderHistoryScanResult");
  return {
    workspacePath: text(root.workspace_path, "workspace_path"),
    conversations: wireArray(root.conversations, "conversations").map((value, index) =>
      decodeConversation(value, `conversations[${index}]`, fallbackAuthority, fallbackDeviceId)),
  };
}

export function decodeProviderHistoryScanOperation(
  value: unknown,
): ProviderHistoryScanOperation {
  const root = wireRecord(value, "ProviderHistoryScanOperation");
  const state = oneOf(root.state, ["idle", "scanning", "complete", "failed", "cancelled"] as const, "state");
  const checkpoints = wireArray(root.checkpoints, "checkpoints").map((value, index) => {
    const row = wireRecord(value, `checkpoints[${index}]`);
    return {
      provider: text(row.provider, `checkpoints[${index}].provider`),
      historyRoot: text(row.history_root, `checkpoints[${index}].history_root`),
      scannedFiles: integer(row.scanned_files, `checkpoints[${index}].scanned_files`),
      indexedItems: integer(row.indexed_items, `checkpoints[${index}].indexed_items`),
      lastSourceId: nullableText(row.last_source_id, `checkpoints[${index}].last_source_id`),
    };
  });
  return {
    schemaVersion: integer(root.schema_version, "schema_version"),
    correlationId: text(root.correlation_id, "correlation_id"),
    operationId: text(root.operation_id, "operation_id"),
    state,
    phase: text(root.phase, "phase"),
    importFilter: oneOf(root.import_filter ?? "all", ["all", "unimported", "imported"] as const, "import_filter"),
    checkpoints,
    indexRevision: integer(root.index_revision, "index_revision"),
    revision: integer(root.revision, "revision"),
    createdAt: text(root.created_at, "created_at"),
    updatedAt: text(root.updated_at, "updated_at"),
    errorCode: nullableText(root.error_code, "error_code"),
  };
}

export function decodeProviderHistoryConversationPage(
  value: unknown,
  fallbackAuthority: AuthorityRef,
  fallbackDeviceId?: string,
): ProviderHistoryConversationPage {
  const root = wireRecord(value, "ProviderHistoryConversationPage");
  return {
    conversation: decodeConversation(root.conversation, "conversation", fallbackAuthority, fallbackDeviceId),
    messages: wireArray(root.messages, "messages").map((value, index) => {
      const message = wireRecord(value, `messages[${index}]`);
      const role = oneOf(message.role, ["user", "assistant", "tool", "system"] as const, `messages[${index}].role`);
      return {
        messageId: text(message.message_id, `messages[${index}].message_id`),
        ordinal: integer(message.ordinal, `messages[${index}].ordinal`),
        role,
        text: text(message.text, `messages[${index}].text`),
        createdAt: nullableText(message.created_at, `messages[${index}].created_at`),
      };
    }),
    nextCursor: nullableInteger(root.next_cursor, "next_cursor"),
  };
}

export function decodeProviderHistoryImportResult(
  value: unknown,
  fallbackAuthority: AuthorityRef,
): ProviderHistoryImportResult {
  const root = wireRecord(value, "ProviderHistoryImportResponse");
  const imports = wireArray(root.imports, "imports").map((value, index) => {
    const item = wireRecord(value, `imports[${index}]`);
    return {
      provider: providerId(item.provider, `imports[${index}].provider`),
      sourceId: text(item.source_id, `imports[${index}].source_id`),
      conversationId: text(item.conversation_id, `imports[${index}].conversation_id`),
      created: boolean(item.created, `imports[${index}].created`),
      session: projectSession(
        decodeSessionSnapshot(item.session, `imports[${index}].session`),
        fallbackAuthority,
      ),
    };
  });
  return {
    registryRevision: integer(root.registry_revision, "registry_revision"),
    imports,
    sessions: imports.map((item) => item.session),
  };
}

function decodeWorkspace(
  value: unknown,
  path: string,
  historyHomeDirectory: string | undefined,
  fallbackAuthority: AuthorityRef,
  fallbackDeviceId?: string,
): ProviderHistoryWorkspace {
  const row = wireRecord(value, path);
  return {
    workspacePath: text(row.workspace_path, `${path}.workspace_path`),
    displayName: text(row.display_name, `${path}.display_name`),
    conversationCount: integer(row.conversation_count, `${path}.conversation_count`),
    updatedAt: nullableText(row.updated_at, `${path}.updated_at`),
    conversations: wireArray(row.conversations, `${path}.conversations`).map((value, index) =>
      decodeConversation(value, `${path}.conversations[${index}]`, fallbackAuthority, fallbackDeviceId, historyHomeDirectory)),
  };
}

function decodeConversation(
  value: unknown,
  path: string,
  fallbackAuthority: AuthorityRef,
  fallbackDeviceId?: string,
  historyHomeDirectory?: string,
): ProviderHistoryConversation {
  const row = wireRecord(value, path);
  const imported = row.imported === true;
  const sessionId = optionalText(row.imported_session_id, `${path}.imported_session_id`) ?? null;
  if (imported && !sessionId) throw contractError(`${path}.imported_session_id is required when imported`);
  const decodedAuthority = row.imported_authority === undefined || row.imported_authority === null
    ? null
    : authority(row.imported_authority, `${path}.imported_authority`);
  const importedAuthority = imported
    ? normalizeImportedAuthority(decodedAuthority ?? fallbackAuthority, fallbackAuthority)
    : null;
  const importedDeviceId = imported
    ? optionalText(row.imported_device_id, `${path}.imported_device_id`)
      ?? (importedAuthority?.kind === "device" ? importedAuthority.deviceId : fallbackDeviceId ?? null)
    : null;
  const identity = decodeAgentIdentity({ ...row, provider_id: row.provider }, path);
  return {
    sourceId: text(row.source_id, `${path}.source_id`),
    provider: providerId(row.provider, `${path}.provider`),
    historyHomeDirectory,
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    conversationId: text(row.conversation_id, `${path}.conversation_id`),
    workspacePath: text(row.workspace_path, `${path}.workspace_path`),
    title: text(row.title, `${path}.title`),
    preview: text(row.preview, `${path}.preview`),
    messageCount: integer(row.message_count, `${path}.message_count`),
    userMessageCount: integer(row.user_message_count, `${path}.user_message_count`),
    summaryTruncated: boolean(row.summary_truncated, `${path}.summary_truncated`),
    startedAt: nullableText(row.started_at, `${path}.started_at`),
    updatedAt: nullableText(row.updated_at, `${path}.updated_at`),
    resumeSupported: boolean(row.resume_supported, `${path}.resume_supported`),
    imported,
    importedSessionId: sessionId,
    importedAuthority,
    importedDeviceId,
  };
}

function authority(value: unknown, path: string): AuthorityRef {
  const row = wireRecord(value, path);
  if (row.kind === "current_device") return { kind: "current_device" };
  if (row.kind === "device") return { kind: "device", deviceId: text(row.device_id, `${path}.device_id`) };
  if (row.kind === "cloud_user") return { kind: "cloud_user", userId: text(row.user_id, `${path}.user_id`) };
  if (row.kind === "cloud_organization") {
    return { kind: "cloud_organization", organizationId: text(row.organization_id, `${path}.organization_id`) };
  }
  throw contractError(`${path}.kind is invalid`);
}

function normalizeImportedAuthority(value: AuthorityRef, fallback: AuthorityRef): AuthorityRef {
  return value.kind === "current_device" && fallback.kind === "device" ? fallback : value;
}

function providerId(value: unknown, path: string): ProviderId {
  return decodeProviderId(value, path);
}
function text(value: unknown, path: string): string {
  if (typeof value !== "string") throw contractError(`${path} must be text`);
  return value;
}
function optionalText(value: unknown, path: string): string | undefined {
  return value === undefined || value === null ? undefined : text(value, path);
}
function nullableText(value: unknown, path: string): string | null {
  return value === undefined || value === null ? null : text(value, path);
}
function integer(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) throw contractError(`${path} must be a non-negative integer`);
  return value as number;
}
function nullableInteger(value: unknown, path: string): number | null {
  return value === undefined || value === null ? null : integer(value, path);
}
function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw contractError(`${path} must be boolean`);
  return value;
}
function oneOf<const T extends readonly string[]>(value: unknown, options: T, path: string): T[number] {
  if (typeof value !== "string" || !options.includes(value)) throw contractError(`${path} is invalid`);
  return value as T[number];
}
function contractError(message: string): TypeError {
  return new TypeError(`Provider history contract is invalid: ${message}`);
}
